/**
 * CreativeTypesPage — W9-69 dash surface for slug `creative-types`.
 *
 * Minimal CRUD view over the merged platform + tenant catalog, complementing
 * the richer `/creatives/types` RHF-style surface. Lists current types,
 * allows registering a new tenant override, and exposes a delete action that
 * reverts a code back to the platform default (or removes a custom type).
 *
 * Relies on the hooks exported from `./api` (useCreativeTypes,
 * useCreateCreativeType, useDeleteCreativeType) so the fetch layer stays
 * confined to `api.ts` per dash contract D6.
 *
 * Owned file lane: this file + CreativeTypesPage.test.tsx +
 * ../../routes/creative-types.tsx. `api.ts` and `TypesList.tsx` are read-only
 * here (touched by the earlier OPUS-D17 lane).
 */
import { useState, type FormEvent } from "react";
import {
	useCreativeTypes,
	useCreateCreativeType,
	useDeleteCreativeType,
	parseMimeList,
	parseBytes,
	formatBytes,
	type CreativeTypeView,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-transparent px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

export function CreativeTypesPage() {
	const listQuery = useCreativeTypes();
	const createMut = useCreateCreativeType();
	const deleteMut = useDeleteCreativeType();

	const [code, setCode] = useState("");
	const [label, setLabel] = useState("");
	const [mimesRaw, setMimesRaw] = useState("");
	const [sizeRaw, setSizeRaw] = useState("");
	const [localError, setLocalError] = useState<string | null>(null);

	const resetForm = () => {
 setCode("");
 setLabel("");
 setMimesRaw("");
 setSizeRaw("");
 setLocalError(null);
	};

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 setLocalError(null);
 const trimmedCode = code.trim().toLowerCase();
 const trimmedLabel = label.trim();
 if (trimmedCode === "") {
 setLocalError("code is required");
 return;
 }
 if (trimmedLabel === "") {
 setLocalError("label is required");
 return;
 }
 const mimes = parseMimeList(mimesRaw);
 if (mimes.length === 0) {
 setLocalError("at least one MIME is required");
 return;
 }
 let maxSizeBytes: number | null = null;
 if (sizeRaw.trim() !== "") {
 const parsed = parseBytes(sizeRaw);
 if (parsed === null || Number.isNaN(parsed)) {
 setLocalError("max size invalid (bytes or e.g. 500KB / 2MB)");
 return;
 }
 maxSizeBytes = parsed;
 }
 createMut.mutate(
 { code: trimmedCode, label: trimmedLabel, allowedMimes: mimes, maxSizeBytes },
 { onSuccess: resetForm },
 );
	};

	const types: CreativeTypeView[] = listQuery.data?.types ?? [];
	const serverError =
 createMut.error instanceof Error ? createMut.error.message : null;
	const listError =
 listQuery.error instanceof Error ? listQuery.error.message : null;

	return (
 <main
 data-testid="creative-types-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 creative-types
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Creative types
 </h1>
 <p className="text-sm text-zinc-600">
 Merged catalog of platform defaults + tenant overrides.
 </p>
 </header>

 <section className={CARD} aria-label="Create creative type">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Register type</h2>
 <form
 onSubmit={handleCreate}
 className="mt-3 grid gap-3 sm:grid-cols-2"
 data-testid="creative-types-create-form"
 noValidate
 >
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
 Code
 <input
 type="text"
 className={INPUT}
 placeholder="e.g. banner_v2"
 value={code}
 onChange={(e) => setCode(e.currentTarget.value)}
 disabled={createMut.isPending}
 autoComplete="off"
 spellCheck={false}
 data-testid="creative-types-code-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
 Label
 <input
 type="text"
 className={INPUT}
 placeholder="Hero banner"
 value={label}
 onChange={(e) => setLabel(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="creative-types-label-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-600 sm:col-span-2">
 Allowed MIME types
 <input
 type="text"
 className={INPUT}
 placeholder="image/png, image/jpeg"
 value={mimesRaw}
 onChange={(e) => setMimesRaw(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="creative-types-mimes-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
 Max size
 <input
 type="text"
 className={INPUT}
 placeholder="500KB (leave empty for no max)"
 value={sizeRaw}
 onChange={(e) => setSizeRaw(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="creative-types-size-input"
 />
 </label>
 <div className="flex items-end">
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={createMut.isPending}
 data-testid="creative-types-submit"
 >
 {createMut.isPending ? "Saving..." : "Create"}
 </button>
 </div>
 </form>
 {(localError || serverError) ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="creative-types-error"
 >
 {localError ?? serverError}
 </p>
 ) : null}
 </section>

 <section className={CARD} aria-label="Creative types list">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Catalog</h2>
 {listQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="creative-types-loading"
 >
 Loading.
 </p>
 ) : listError ? (
 <p
 className="mt-3 text-sm text-red-600"
 role="alert"
 data-testid="creative-types-list-error"
 >
 {listError}
 </p>
 ) : types.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="creative-types-empty"
 >
 No creative types yet.
 </p>
 ) : (
 <ul
 className="mt-3 divide-y divide-zinc-200"
 data-testid="creative-types-list"
 >
 {types.map((t) => (
 <li
 key={t.code}
 className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
 data-testid={`creative-types-row-${t.code}`}
 >
 <div className="flex flex-col">
 <div className="flex items-center gap-2">
 <span className="font-mono text-xs text-zinc-900">
 {t.code}
 </span>
 <span
 className={
 t.source === "tenant"
 ? "rounded-[4px] border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-green-700"
 : "rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-600"
 }
 >
 {t.source}
 </span>
 {t.shadows_platform ? (
 <span className="rounded-[4px] border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-green-700">
 shadow
 </span>
 ) : null}
 </div>
 <span className="text-xs text-zinc-500">{t.label}</span>
 <span className="mt-0.5 font-mono text-[11px] text-zinc-500">
 {t.allowed_mimes.join(",")}
 </span>
 <span className="text-[11px] text-zinc-500">
 max: {formatBytes(t.max_size_bytes)}
 </span>
 </div>
 {t.source === "tenant" ? (
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => deleteMut.mutate(t.code)}
 disabled={deleteMut.isPending}
 data-testid={`creative-types-delete-${t.code}`}
 >
 {t.shadows_platform ? "Revert" : "Delete"}
 </button>
 ) : null}
 </li>
 ))}
 </ul>
 )}
 </section>
 </main>
	);
}

export default CreativeTypesPage;
